import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCookie } from "../../utilities/cookies";
import GatewayShell from "../../gateway/GatewayShell";
import GatewayAction from "../../gateway/GatewayAction";
import GatewayCodeInput from "../../gateway/GatewayCodeInput";
import GatewayNotice from "../../gateway/GatewayNotice";
import GatewayQrCode from "../../gateway/GatewayQrCode";

export default function SecurityEnrollment() {
  const navigate = useNavigate();
  const session = getCookie("user");
  const [enrollment, setEnrollment] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [showSetupKey, setShowSetupKey] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const token = session?.token;
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }
    fetch(`${process.env.REACT_APP_BASE_URL}/api/auth/security/authenticator/enroll`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    })
      .then(async (response) => {
        const payload = await response.json();
        if (!response.ok || !payload.success) throw new Error(payload.message || "Authenticator setup could not begin");
        return payload.enrollment;
      })
      .then(setEnrollment)
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [navigate, session?.token]);

  const confirm = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/auth/security/authenticator/confirm`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || "Authenticator setup could not be confirmed");
      setBackupCodes(payload.backupCodes || []);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GatewayShell stage="security-verification" focused onBack={() => navigate(-1)}>
      <form className="gatewayCard" onSubmit={confirm} noValidate>
        <h2>{backupCodes.length ? "Save your backup codes" : "Set up an authenticator"}</h2>
        <p className="gatewayCard__sub">
          {backupCodes.length ? "Store these single-use codes somewhere private." : "Add Clikkle to your authenticator app, then enter the current code."}
        </p>
        {backupCodes.length ? (
          <div className="gatewayBackupCodes" aria-label="Backup codes">
            {backupCodes.map((backupCode) => <code key={backupCode}>{backupCode}</code>)}
          </div>
        ) : (
          <>
            <GatewayQrCode value={enrollment?.otpAuthUri} />
            <p className="gatewayEnrollmentHelp">Open Google Authenticator, Microsoft Authenticator, 1Password, Bitwarden, or another TOTP app. Choose <strong>Add account</strong>, then scan this QR code.</p>
            <button type="button" className="gatewayTextAction" onClick={() => setShowSetupKey((visible) => !visible)}>{showSetupKey ? "Hide setup key" : "Can’t scan it? Show setup key"}</button>
            {showSetupKey ? (
              <GatewayNotice title="Manual setup key">
                <div className="gatewaySetupKeyRow">
                  <span className="gatewaySetupKey">{loading ? "Preparing securely…" : enrollment?.secret || "Unavailable"}</span>
                  <button type="button" className="gatewayCopyKey" onClick={async () => {
                    if (!enrollment?.secret) return;
                    await navigator.clipboard.writeText(enrollment.secret);
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 1600);
                  }}>{copied ? "Copied" : "Copy"}</button>
                </div>
                <span className="gatewaySetupKeyHint">Account type: Time based (TOTP)</span>
              </GatewayNotice>
            ) : null}
            <GatewayCodeInput name="enrollmentCode" label="Enter the 6-digit code" placeholder="000000" value={code} onChange={(event) => setCode(event.target.value)} error={error} autoFocus />
          </>
        )}
        {backupCodes.length ? (
          <GatewayAction type="button" onClick={() => navigate("/security-center")}>Done</GatewayAction>
        ) : (
          <GatewayAction type="submit" disabled={loading || code.length !== 6}>{loading ? "Preparing securely…" : "Confirm authenticator"}</GatewayAction>
        )}
      </form>
    </GatewayShell>
  );
}
